import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import WarningIcon from '@material-ui/icons/Warning';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import BlockIcon from '@material-ui/icons/Block';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    fontSize: 60,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  vehicleIcon: {
    fontSize: 60,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  warningBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

const VehicleDeactivatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchVehicle();
  }, [id]);

  const fetchVehicle = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const vehicleDoc = doc(firestore, 'vehicles', id);
      const vehicleSnapshot = await getDoc(vehicleDoc);
      
      if (vehicleSnapshot.exists()) {
        setVehicle({
          id: vehicleSnapshot.id,
          ...vehicleSnapshot.data()
        });
      } else {
        // Si le véhicule n'existe pas, utiliser des données de démonstration
        setVehicle({
          id: id,
          name: 'Bus Premium',
          registrationNumber: 'AB-123-CD',
          type: 'Bus',
          capacity: 50,
          status: 'Active',
          companyId: '1',
          companyName: 'Voyage Express',
          manufactureYear: 2020,
          model: 'Mercedes Tourismo',
        });
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données du véhicule',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmDeactivate = async () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const vehicleDoc = doc(firestore, 'vehicles', id);
      await updateDoc(vehicleDoc, {
        status: 'Inactive',
        deactivatedAt: new Date(),
        deactivatedReason: 'Désactivé par l\'administrateur'
      });
      
      setSnackbar({
        open: true,
        message: 'Véhicule désactivé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/vehicles');
      }, 1500);
      
    } catch (error) {
      console.error('Error deactivating vehicle:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la désactivation du véhicule',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancelDeactivate = () => {
    setOpenConfirmDialog(false);
  };

  const handleBack = () => {
    navigate('/admin/vehicles');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getVehicleTypeIcon = () => {
    return <DirectionsCarIcon className={classes.vehicleIcon} />;
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Désactiver un véhicule
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : vehicle ? (
        <Paper className={classes.paper}>
          <Box className={classes.warningBox}>
            {getVehicleTypeIcon()}
            <BlockIcon className={classes.warningIcon} />
            <Typography variant="h5" gutterBottom>
              Vous êtes sur le point de désactiver le véhicule suivant :
            </Typography>
            <Typography variant="h6" gutterBottom>
              {vehicle.name}
            </Typography>
            <Typography variant="body1" paragraph>
              Immatriculation: {vehicle.registrationNumber}
            </Typography>
            <Typography variant="body1" paragraph>
              Type: {vehicle.type}
            </Typography>
            <Typography variant="body1" paragraph>
              Capacité: {vehicle.capacity} places
            </Typography>
            <Typography variant="body1" paragraph>
              Entreprise: {vehicle.companyName}
            </Typography>
            <Typography variant="body1" paragraph>
              Statut actuel: {vehicle.status}
            </Typography>
            
            <Typography variant="body1" paragraph style={{ marginTop: 16 }}>
              <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8, color: '#f44336' }} />
              La désactivation d'un véhicule aura les conséquences suivantes :
            </Typography>
            
            <Box textAlign="left" width="100%" maxWidth="600px" margin="0 auto">
              <Typography variant="body1" paragraph>
                • Le véhicule ne pourra plus être sélectionné pour de nouveaux voyages
              </Typography>
              <Typography variant="body1" paragraph>
                • Les voyages déjà programmés avec ce véhicule devront être réaffectés
              </Typography>
              <Typography variant="body1" paragraph>
                • Le véhicule restera visible dans la liste mais sera marqué comme inactif
              </Typography>
              <Typography variant="body1" paragraph>
                • Les statistiques du véhicule seront conservées
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph style={{ marginTop: 16 }}>
              Cette action peut être annulée ultérieurement en modifiant le statut du véhicule.
            </Typography>
            
            <Grid container spacing={2} justifyContent="center" style={{ marginTop: 16 }}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                >
                  Annuler
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDeactivate}
                >
                  Désactiver le véhicule
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Véhicule non trouvé
          </Typography>
        </Paper>
      )}
      
      {/* Dialog de confirmation */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelDeactivate}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la désactivation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir désactiver le véhicule {vehicle?.name} ({vehicle?.registrationNumber}) ? 
            Cette action affectera tous les voyages programmés avec ce véhicule.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeactivate} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDeactivate} color="secondary" autoFocus>
            Confirmer la désactivation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleDeactivatePage;
